import { Component, Input, OnInit } from '@angular/core';
import map from 'lodash-es/map.js';
import _ from 'lodash-es';
import {
	DisplayItem,
	type IUserSearch,
	Location,
	StatusDisplayItem,
	LocationWithCountAndCanonicalString,
	SectorDisplayItem,
	SectorWithCountAndCanonicalString,
	StatusWithCount,
} from 'company-finder-common';
import { SearchService } from '@Common';
import { getCountOfCompaniesForSavedSearchUtil } from '@Common';
import { CompanyService } from '@Common';
import { ComponentBase } from '@Common';
import { DeploymentContext } from '@Common';

export interface SectorWithCountAndCanonicalStringAndIds
	extends SectorWithCountAndCanonicalString {
	sectorId: string;
	parentSectorId?: string;
	displayName: string;
}

@Component({
	template: '',
	standalone: false,
})
export class SavedSearchBaseComponent extends ComponentBase implements OnInit {
	@Input() search: IUserSearch;
	public matchedCompaniesCount: number;

	public allStatusesWithCounts: StatusWithCount[] = [];

	protected allSectorsWithCounts: SectorWithCountAndCanonicalString[] = [];
	protected allSectorsWithCountsAndIds: SectorWithCountAndCanonicalStringAndIds[] =
		[];
	protected allLocationsWithCounts: LocationWithCountAndCanonicalString[] =
		[];
	protected allStatuses: StatusDisplayItem[];

	private _allLocations: Location[] = [];

	constructor(
		dc: DeploymentContext,
		private _searchService: SearchService,
		protected _companyService: CompanyService
	) {
		super(dc);
	}

	public async ngOnInit(): Promise<void> {
		const summary = this._deploymentContext.comprehensiveSummary;
		this.allSectorsWithCounts = summary.allSectorsWithCounts;
		this.allSectorsWithCountsAndIds = this.allSectorsWithCounts.map((x) => {
			const sectorWithCountAndCanonicalStringAndIds = _.merge(
				{
					sectorId: x.sector,
					parentSectorId: x.parentSector,
					displayName: x.sector,
				},
				x
			) as SectorWithCountAndCanonicalStringAndIds;
			return sectorWithCountAndCanonicalStringAndIds;
		});
		this._allLocations = summary.locations;
		this.allLocationsWithCounts = _.sortBy(
			summary.locations.map((location) => ({
				location: location,
				count: summary.locationStatusCounts[location.name]?.count ?? 0,
				canonicalString: location.name.toLowerCase(),
			})),
			(locationEntry) => locationEntry.location.name
		);

		this.allStatusesWithCounts = summary.allStatusesWithCounts;

		this.getCountOfCompaniesForSavedSearch();
	}

	public get summary(): DisplayItem[][] {
		return [
			this.locationSummary.map((loc) => ({
				displayName: loc.name,
				...loc,
			})),
			this.sectorSummary,
			this.statusSummary,
		];
	}

	public get locationSummary(): Location[] {
		if (this.search?.locations) {
			// Get location display name
			return this._allLocations.filter(({ name: location }) =>
				this.search.locations.some(
					(userLocation) => location === userLocation.locationId
				)
			);
		}
		return [];
	}

	public set locationSummary(locationDisplayItems: Location[]) {
		this.search.locations = map(
			locationDisplayItems,
			({ name: location }) => ({
				locationId: location,
			})
		);
	}

	public get sectorSummary(): SectorDisplayItem[] {
		if (this.search?.sectors?.length > 0) {
			return this.search.sectors.map((sector) => {
				const sectorItem = {
					sectorId: sector.sectorId,
					parentSectorId: sector.parentSectorId,
					displayName: sector.sectorId,
					// Somewhat redundant properties to bridge an impedance gap in how sectors are selected.
					// The most significant gap is that the newer visual designs require sector.subSectors
					// be populated, and the DisplayItem model used by the dropdown menus doesn't include them at all.
					// See also: ADJQ-1141 & SavedSearchSectorSelectorComponent.deselectItem()
					parentSector: null,
					sector: sector.sectorId,
					subSectors: null,
					canonicalString: sector.sectorId.toLowerCase(),
				};
				// Populate the "extra data"
				const extraInfo = this.allSectorsWithCountsAndIds.find(
					(s) => s.sectorId === sector.sectorId
				);
				if (extraInfo) {
					sectorItem.subSectors = extraInfo.subSectors;
					sectorItem.parentSector = extraInfo.parentSector;
				}
				return sectorItem;
			});
		}
		return [];
	}

	public set sectorSummary(sectorDisplayItems: SectorDisplayItem[]) {
		this.search.sectors = sectorDisplayItems;
	}

	public get statusSummary(): StatusDisplayItem[] {
		if (!(this.search?.statuses?.trim().length > 0)) {
			return [];
		}

		return this.search.statuses.split(',').map((status) => ({
			status: status,
			displayName: this._deploymentContext.getStatusDisplayName(status),
		}));
	}

	public set statusSummary(statusDisplayItems: StatusDisplayItem[]) {
		this.search.statuses = statusDisplayItems
			.map((status) => status.status)
			.toString();
	}

	public async getCountOfCompaniesForSavedSearch(): Promise<void> {
		if (this.search) {
			this.matchedCompaniesCount =
				await getCountOfCompaniesForSavedSearchUtil(
					this.search,
					this._companyService
				);
		}
	}
}
